import StartupPageTitle from './StartupPageTitle';
/**
 * MarketplaceTabsShell.jsx — iter 50, credit reworked in iter 70.
 *
 * Wraps the Services + Credit founder areas.
 *
 *   Services — a marketplace. Two tabs:
 *     1. Marketplace  (browse products, pick a partner, guided request wizard)
 *     2. Engagements  (in-flight requests)
 *
 *   Credit — NOT a marketplace. Founderboard takes one application to its
 *   lending panel, so there is nothing for a founder to browse or pick. The
 *   index route IS the application; there is no product grid at all (iter 70
 *   removed it). Two tabs:
 *     1. Apply for Credit  (the application, then its status + thread)
 *     2. Engagements       (legacy "Request intro" credit rows, pre-iter-69)
 *
 * The credit product catalog still exists and is still the source of the
 * "what kind of credit" multi-select on the application — it's just no longer
 * browsable as a grid. See _credit_product_options in server.py.
 *
 * Marketing has its own MarketingShell (with Metrics + Brand Brain tabs) which
 * adds its own Engagements tab — see MarketingShell.jsx.
 */
import React from 'react';
import './startup-services.css';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Storefront, Briefcase, Bank } from '@phosphor-icons/react';
import MarketplaceShell from './MarketplaceShell';
import Engagements from '../pages/startup/screens/Engagements';
import VendorProfile from '../pages/VendorProfile';
import ServiceRequestWizard from '../pages/startup/screens/ServiceRequestWizard';
import CreditApplication from '../pages/startup/screens/CreditApplication';

const CONFIG = {
  service: {
    base:   '/startup/services',
    accent: '#0d9488',
    label:  'Services',
    testidPrefix: 'services',
  },
  credit: {
    base:   '/startup/credit',
    accent: '#f59e0b',
    label:  'Credit',
    testidPrefix: 'credit',
  },
};

const MarketplaceTabsShell = ({ category }) => {
  const cfg = CONFIG[category];
  const location = useLocation();

  const isCredit = category === 'credit';

  // `activeWhen` is explicit per tab rather than a shared exact/startsWith flag:
  // credit's first tab lives on the base path, so a naive startsWith would light
  // it up on /engagements too.
  const TABS = [
    // (Iter 70) Credit's first tab is the application, on the base path — there
    // is no product grid to browse any more. /application stays routable because
    // the notification emails deep-link to it.
    isCredit
      ? { path: cfg.base, label: 'Apply for Credit', icon: Bank, testid: 'credit-tab-application',
          activeWhen: (p) => p === cfg.base || p === `${cfg.base}/application` }
      : { path: cfg.base, label: 'Marketplace', icon: Storefront, testid: `${cfg.testidPrefix}-tab-marketplace`,
          activeWhen: (p) => p === cfg.base },
    { path: `${cfg.base}/engagements`, label: 'Engagements', icon: Briefcase,
      testid: `${cfg.testidPrefix}-tab-engagements`,
      activeWhen: (p) => p.startsWith(`${cfg.base}/engagements`) },
  ];

  // Hide tabs on focused sub-routes: vendor profile detail, and the guided
  // service request wizard (a linear flow shouldn't offer sideways exits).
  // The credit application keeps its tabs — a founder mid-form may well want to
  // go and look at what the products actually are.
  const showTabs = !/\/(services|credit)\/([a-f0-9-]{8,}|request\/)/.test(location.pathname);

  return (
    <div className={category === 'service' ? 'fb-services flex flex-col min-h-full' : 'flex flex-col min-h-full'}>
      {showTabs && (
        <div className={category === 'service' ? 'services-page-header' : 'border-b border-border bg-background sticky top-0 z-10'} data-testid={`${cfg.testidPrefix}-shell-tabs`}>
          <div className={category === 'service' ? 'services-content' : 'max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8'}>
            {category === 'service' && <div className="services-page-title"><StartupPageTitle icon={Storefront}>Services</StartupPageTitle><p>Flexible expert support, without the agency overhead.</p></div>}
            <div className={category === 'service' ? 'services-page-tabs' : 'flex items-center gap-1 overflow-x-auto'}>
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = t.activeWhen(location.pathname);
                return (
                  <Link
                    key={t.path}
                    to={t.path}
                    data-testid={t.testid}
                    aria-current={active ? 'page' : undefined}
                    className={category === 'service' ? `services-page-tab ${active ? 'is-active' : ''}` : `relative px-4 py-3 text-sm font-medium inline-flex items-center gap-1.5 whitespace-nowrap ${active ? 'text-text-primary' : 'text-text-muted hover:text-text-secondary'}`}
                  >
                    <Icon size={13} weight="bold" /> {t.label}
                    {active && category !== 'service' && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: cfg.accent }} />}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <Routes>
          {/* (Iter 70) Credit's index is the application, not a product grid.
              MarketplaceShell is never rendered for credit any more. */}
          <Route path="/" element={isCredit ? <CreditApplication /> : <MarketplaceShell category={category} />} />
          <Route path="/engagements" element={<Engagements category={category} />} />
          {/* (Iter 65) Guided purchase flow — services only. Declared above the
              /:vendorId catch so a request id is never read as a vendor id. */}
          {category === 'service' && (
            <Route path="/request/:requestId" element={<ServiceRequestWizard />} />
          )}
          {/* Kept routable: the application-received, status-change and thread
              emails all deep-link to /startup/credit/application. Unlike the
              index (which lands on the credit home screen — see the note in
              CreditApplication) this one opens the application itself, since
              that's what the email said it would do. */}
          {isCredit && <Route path="/application" element={<CreditApplication openApplication />} />}
          <Route path="/:vendorId" element={<VendorProfile mode={category} />} />
          <Route path="*" element={<Navigate to={cfg.base} replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default MarketplaceTabsShell;
